import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import { ArrowDownRight, ArrowRight, ArrowUpRight, CalendarDays, ChevronDown, ChevronLeft, ChevronRight, Copy, Share2 } from 'lucide-react';
import { HostBottomNav } from '../../components/HostBottomNav';
import { HostAirbnbSyncSettingsCard } from '../../components/host/HostAirbnbSyncSettingsCard';
import { HostLimitedAccessCard } from '../../components/host/HostLimitedAccessCard';
import NotificationBellButton from '../../components/NotificationBellButton';
import WelcomeTourSheet from '../../components/WelcomeTourSheet';
import PageInfoButton from '../../components/PageInfoButton';
import { hostWelcomeTourSlides, hostInviteTourSlides } from '../../components/welcomeTourSlides';
import { hasSeenWelcomeTour, markWelcomeTourSeen } from '../../utils/welcomeTourState';
import { fetchMyHostListings, getInviteEligibleHostListings, saveMyHostListingSyncSettings } from '../../utils/hostListings';
import { createInvite, getHostInvites, syncHostDetectedReservations } from '../../utils/inviteApi';
import { formatEuropeanDate, toInputDateValue } from '../../utils/dateFormat';
import { DEFAULT_HOST_SYNC_SETTINGS, normalizeHostSyncSettings } from '../../utils/hostSyncSettings';
import { trackInviteCreated, trackInviteShared } from '../../utils/airbridge';
import { mintShareLink } from '../../utils/airbridgeShareLink';
import { buildShareUrl } from '../../utils/shareBaseUrl';
import { shareOrCopy } from '../../utils/shareOrCopy';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import {
  PremiumRangeDatePicker,
  buildBlockedCheckInDateSet,
  buildListingAwareBlockedDateSet,
  rangeTouchesBlockedDates,
} from './invitePage/PremiumRangeDatePicker';

const copyText = async (text) => {
  if (!text) return false;

  if (navigator.clipboard && window.isSecureContext) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      // continue to fallback
    }
  }

  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    textArea.style.pointerEvents = 'none';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    textArea.setSelectionRange(0, textArea.value.length);
    const copied = document.execCommand('copy');
    document.body.removeChild(textArea);
    return copied;
  } catch {
    return false;
  }
};

export default function HostGenerateQRPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user] = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));
  const [hostListings, setHostListings] = useState([]);
  const [selectedListingId, setSelectedListingId] = useState('');
  const [checkInDate, setCheckInDate] = useState('');
  const [checkoutDate, setCheckoutDate] = useState('');
  const [hostInvites, setHostInvites] = useState([]);
  const [generatedInvite, setGeneratedInvite] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listingsLoading, setListingsLoading] = useState(true);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showSyncSettings, setShowSyncSettings] = useState(() => Boolean(location.state?.openSyncSettings));
  const [syncSettings, setSyncSettings] = useState(DEFAULT_HOST_SYNC_SETTINGS);
  const [syncSettingsDirty, setSyncSettingsDirty] = useState(false);
  const [syncSettingsSaving, setSyncSettingsSaving] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [pageTourOpen, setPageTourOpen] = useState(false);
  const [manualCopyLink, setManualCopyLink] = useState(null);
  const [listingPickerOpen, setListingPickerOpen] = useState(false);
  const manualLinkInputRef = useRef(null);
  const rangeFieldRef = useRef(null);
  const syncSettingsAnchorRef = useRef(null);
  const listingPickerRef = useRef(null);
  const generatedInviteRef = useRef(null);
  const isLimitedAccessHost = user?.host_status === 'rejected';

  // Close the listing picker dropdown on outside tap.
  useEffect(() => {
    if (!listingPickerOpen) return undefined;
    const handler = (event) => {
      if (listingPickerRef.current && !listingPickerRef.current.contains(event.target)) {
        setListingPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [listingPickerOpen]);

  useEffect(() => {
    const roleSeen = hasSeenWelcomeTour('host');
    const pageSeen = hasSeenWelcomeTour('host-page-invite');
    if (!roleSeen) {
      setTourOpen(true);
    } else if (!pageSeen) {
      setPageTourOpen(true);
    }
  }, []);

  const handleCloseTour = () => {
    setTourOpen(false);
    markWelcomeTourSeen('host');
    // Avoid double-prompting on first ever visit: mark page tour seen too.
    markWelcomeTourSeen('host-page-invite');
  };

  const handleClosePageTour = () => {
    setPageTourOpen(false);
    markWelcomeTourSeen('host-page-invite');
  };

  useEffect(() => {
    if (location.state?.openSyncSettings && syncSettingsAnchorRef.current) {
      // Defer to next paint so the section is mounted before scrolling.
      requestAnimationFrame(() => {
        syncSettingsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.state]);

  const availableListings = useMemo(() => getInviteEligibleHostListings(hostListings), [hostListings]);
  const selectedListing = useMemo(() => availableListings.find((listing) => listing.id === selectedListingId) || availableListings[0] || null, [availableListings, selectedListingId]);
  const selectedListingLocked = Boolean(selectedListing && !selectedListing.airbnbSettingsCompleted);

  const openSyncSettingsForSelected = () => {
    setShowSyncSettings(true);
    requestAnimationFrame(() => {
      syncSettingsAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  useEffect(() => {
    if (!availableListings.length) {
      setSelectedListingId('');
      return;
    }
    if (!availableListings.some((listing) => listing.id === selectedListingId)) {
      const targetId = location.state?.targetListingId;
      const preferred = targetId && availableListings.some((listing) => listing.id === targetId) ? targetId : availableListings[0].id;
      setSelectedListingId(preferred);
    }
  }, [availableListings, selectedListingId, location.state]);

  useEffect(() => {
    setSyncSettings(normalizeHostSyncSettings(selectedListing || DEFAULT_HOST_SYNC_SETTINGS));
    setSyncSettingsDirty(false);
  }, [selectedListing]);

  const blockedRanges = useMemo(() => hostInvites.filter((invite) => {
    if (!selectedListing || invite.listing_id !== selectedListing.listingId) return false;
    if (!invite.check_in_date || !invite.checkout_date) return false;
    return !['declined', 'declined_by_host', 'cancelled_by_ical'].includes(invite.status);
  }), [hostInvites, selectedListing]);

  const blockedDateSet = useMemo(() => buildListingAwareBlockedDateSet(blockedRanges, syncSettings), [blockedRanges, syncSettings]);
  const blockedCheckInDateSet = useMemo(() => buildBlockedCheckInDateSet(blockedRanges), [blockedRanges]);

  const refreshInvites = async () => {
    try {
      if (isLimitedAccessHost) {
        setHostInvites([]);
        return;
      }
      await syncHostDetectedReservations();
      const response = await getHostInvites();
      setHostInvites(response);
    } catch (error) {
      console.error(error);
    }
  };

  const loadHostListings = async () => {
    try {
      const response = await fetchMyHostListings();
      setHostListings(response);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to load your listings');
    } finally {
      setListingsLoading(false);
    }
  };

  useEffect(() => {
    const handleOutside = (event) => {
      if (rangeFieldRef.current && !rangeFieldRef.current.contains(event.target)) {
        setShowCalendar(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, []);

  useEffect(() => {
    refreshInvites();
    loadHostListings();
  }, []);

  useEffect(() => {
    const selectedRangeStillValid = !checkInDate || !checkoutDate || !rangeTouchesBlockedDates(checkInDate, checkoutDate, blockedDateSet);
    if (!selectedRangeStillValid) {
      setCheckInDate('');
      setCheckoutDate('');
    }
  }, [blockedDateSet, checkInDate, checkoutDate]);

  // Auto-scroll to the freshly generated invite card so hosts don't wonder
  // "where's my invite?" after tapping Generate. Waits one frame for the
  // AnimatePresence entrance to mount the node before scrolling.
  useEffect(() => {
    if (!generatedInvite) return;
    const timer = window.setTimeout(() => {
      generatedInviteRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [generatedInvite]);

  const handleGenerateInvite = async () => {
    if (!selectedListing || !checkInDate || !checkoutDate) {
      toast.error('Please choose a listing and reservation dates');
      return;
    }
    if (selectedListingLocked) {
      toast.error("Match this listing's Airbnb settings before creating invites");
      openSyncSettingsForSelected();
      return;
    }
    if (rangeTouchesBlockedDates(checkInDate, checkoutDate, blockedDateSet)) {
      toast.error('Airbnb reservations cannot overlap these dates');
      return;
    }
    setLoading(true);
    try {
      const invite = await createInvite({
        listing_id: selectedListing.listingId,
        listing_name: selectedListing.name,
        listing_image: selectedListing.image,
        listing_address: selectedListing.locationLabel,
        check_in_date: checkInDate,
        checkout_date: checkoutDate,
      });
      const link = buildShareUrl(`/invite/${invite.invite_id}`);
      setGeneratedInvite({ ...invite, link });
      // Airbridge: manual host→guest invite created. Captures host-funnel
      // engagement (created vs shared vs accepted).
      trackInviteCreated({ inviteId: invite.invite_id, inviteType: 'host_to_guest', source: 'manual' });
      toast.success('Invitation link generated!');
      refreshInvites();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate invite');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSyncSettings = async () => {
    if (!selectedListing) {
      return;
    }
    setSyncSettingsSaving(true);
    try {
      const updatedListing = await saveMyHostListingSyncSettings(selectedListing.id, syncSettings);
      setHostListings((current) => current.map((listing) => (listing.id === updatedListing.id ? updatedListing : listing)));
      setSyncSettings(normalizeHostSyncSettings(updatedListing));
      setSyncSettingsDirty(false);
      toast.success('Airbnb sync settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Unable to save Airbnb sync settings');
    } finally {
      setSyncSettingsSaving(false);
    }
  };

  // Fire the Airbridge mint in the background purely for attribution /
  // click tracking. The visible copy/share flow always uses the raw
  // `allurexp.com/invite/<id>` URL so hosts (and their guests) see the
  // clean canonical link — never the `abr.ge/…` short form.
  const kickOffAirbridgeMint = () => {
    if (!generatedInvite?.invite_id || !generatedInvite?.link) return;
    mintShareLink({
      inviteType: 'host_to_guest',
      inviteKey: generatedInvite.invite_id,
      rawUrl: generatedInvite.link,
      ogTitle: 'Your Allure invite',
      ogDescription: 'Tap to unlock your venue discounts in Allure.',
    }).catch(() => {});
  };

  const handleCopy = async () => {
    if (!generatedInvite?.link) return;
    const linkToCopy = generatedInvite.link;
    kickOffAirbridgeMint();
    const copied = await copyText(linkToCopy);
    if (copied) {
      trackInviteShared({ inviteId: generatedInvite.invite_id, inviteType: 'host_to_guest', channel: 'clipboard' });
      toast.success('Invitation link copied');
    } else {
      setManualCopyLink(linkToCopy);
    }
  };

  const handleShare = async () => {
    if (!generatedInvite?.link) return;
    // Fire native share sheet immediately with the raw URL — this keeps
    // the call inside the browser's user-activation window so Chromium
    // WebViews (inside the packaged .aab) don't reject with
    // NotAllowedError. Mint the Airbridge short link in the background
    // for attribution; if it lands quickly enough we use it, otherwise
    // the raw allurexp.com URL is still a valid deep link.
    const rawUrl = generatedInvite.link;
    // Kick off the mint in parallel (fire-and-forget for attribution).
    kickOffAirbridgeMint();
    try {
      const result = await shareOrCopy({
        title: 'Allure Invitation',
        text: 'Open this Allure invitation link',
        url: rawUrl,
      });
      if (result.method === 'share' && !result.cancelled) {
        trackInviteShared({ inviteId: generatedInvite.invite_id, inviteType: 'host_to_guest', channel: 'share_sheet' });
      } else if (result.method === 'copy') {
        trackInviteShared({ inviteId: generatedInvite.invite_id, inviteType: 'host_to_guest', channel: 'clipboard' });
        toast.success('Invitation link copied');
      } else if (result.method === 'failed') {
        setManualCopyLink(rawUrl);
      }
    } catch {
      setManualCopyLink(rawUrl);
    }
  };

  const handleManualCopyRetry = async () => {
    if (!manualCopyLink) return;
    const copied = await copyText(manualCopyLink);
    if (copied) {
      toast.success('Invitation link copied');
      setManualCopyLink(null);
    } else if (manualLinkInputRef.current) {
      manualLinkInputRef.current.focus();
      manualLinkInputRef.current.select();
      toast.info('Long-press the link and tap Copy');
    }
  };

  return (
    <div className="app-shell premium-page" data-testid="host-invite-generator-page" style={{ backgroundColor: 'var(--app-bg)' }}>
      <div style={{ padding: '14px 20px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <PageInfoButton
          onClick={() => setPageTourOpen(true)}
          testId="host-invite-page-info-button"
          ariaLabel="About this page"
        />
        <NotificationBellButton buttonTestId="host-invite-bell-button" popupTestId="host-invite-notification-popup" />
      </div>
      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 0 }}>
        <img src="/allure-logo.png" alt="Allure" style={{ width: 112, height: 98, objectFit: 'contain' }} />

        {!isLimitedAccessHost ? <h1 style={{ fontFamily: "'Inria Serif', serif", fontSize: 20, fontWeight: 700, color: 'var(--text)', marginTop: 16, textAlign: 'center', lineHeight: 1.12, maxWidth: 300 }}>Where will you host this guest?</h1> : null}

        {isLimitedAccessHost ? (
          <div style={{ width: '100%', marginTop: 14 }}>
            <HostLimitedAccessCard onReferVenue={() => navigate('/host/refer-venue')} onInviteHosts={() => navigate('/host/referrals')} testIdPrefix="host-limited-access" />
          </div>
        ) : null}

        <div className="premium-card" style={{ width: '100%', marginTop: 14, borderRadius: 28, padding: '16px 12px 18px' }}>
          {listingsLoading ? <p data-testid="host-invite-listings-loading" style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, color: '#6A6186', textAlign: 'center', margin: '4px 0 8px' }}>Loading your listings...</p> : null}

          {!listingsLoading && availableListings.length ? (
            <div ref={listingPickerRef} style={{ position: 'relative' }}>
              {(() => {
                const displayListing = selectedListing || availableListings[0];
                const hasMultiple = availableListings.length > 1;
                return (
                  <motion.button
                    type="button"
                    onClick={() => hasMultiple && setListingPickerOpen((prev) => !prev)}
                    data-testid={`host-invite-listing-${displayListing.id}`}
                    aria-haspopup={hasMultiple ? 'listbox' : undefined}
                    aria-expanded={hasMultiple ? listingPickerOpen : undefined}
                    disabled={!hasMultiple}
                    className="premium-card-compact premium-card-nested"
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 14,
                      cursor: hasMultiple ? 'pointer' : 'default',
                      padding: '10px 14px 10px 10px',
                      borderRadius: 22,
                      background: 'var(--accent-surface)',
                      textAlign: 'left',
                    }}
                    whileHover={hasMultiple ? { y: -1.5 } : undefined}
                    whileTap={hasMultiple ? { scale: 0.99 } : undefined}
                    layout
                  >
                    <img src={displayListing.image} alt={displayListing.name} style={{ width: 96, height: 68, borderRadius: 14, objectFit: 'cover', flexShrink: 0 }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flex: 1, minWidth: 0 }}>
                      <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, color: 'var(--text)', lineHeight: 1.1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{displayListing.name}</span>
                      {displayListing.locationLabel ? <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 13, color: 'var(--text-2)', lineHeight: 1.1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayListing.locationLabel}</span> : null}
                      {hasMultiple ? (
                        <span data-testid="host-invite-listing-picker-hint" style={{ fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--accent-text-soft)', marginTop: 2, letterSpacing: '0.01em', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          Tap to switch · {availableListings.length} listings
                        </span>
                      ) : null}
                    </div>
                    {hasMultiple ? (
                      <motion.span
                        animate={{ rotate: listingPickerOpen ? 180 : 0 }}
                        transition={{ duration: 0.22, ease: [0.25, 0.1, 0.25, 1] }}
                        style={{ display: 'flex', flexShrink: 0 }}
                      >
                        <ChevronDown size={20} strokeWidth={2.2} color="var(--text-2)" />
                      </motion.span>
                    ) : null}
                  </motion.button>
                );
              })()}

              <AnimatePresence>
                {listingPickerOpen && availableListings.length > 1 ? (
                  <motion.div
                    key="host-invite-listing-picker-menu"
                    data-testid="host-invite-listing-picker-menu"
                    role="listbox"
                    initial={{ opacity: 0, y: -8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -6, scale: 0.98 }}
                    transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 10px)',
                      left: 0,
                      right: 0,
                      background: 'var(--surface)',
                      borderRadius: 22,
                      border: '1px solid var(--card-border)',
                      boxShadow: 'var(--premium-shadow-main)',
                      padding: 8,
                      // Cap the picker at ~7 listings before scrolling kicks
                      // in. Each row is ≈ 68px (48px thumb + 10+10 padding),
                      // so 7 rows + 8+8 outer padding ≈ 492px.
                      maxHeight: 492,
                      overflowY: 'auto',
                      zIndex: 30,
                    }}
                  >
                    {availableListings.map((listing) => {
                      const active = selectedListing?.id === listing.id;
                      return (
                        <motion.button
                          key={listing.id}
                          type="button"
                          onClick={() => {
                            setSelectedListingId(listing.id);
                            setListingPickerOpen(false);
                          }}
                          data-testid={`host-invite-listing-picker-option-${listing.id}`}
                          role="option"
                          aria-selected={active}
                          whileTap={{ scale: 0.99 }}
                          style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            padding: '10px 12px',
                            border: 'none',
                            background: active ? 'var(--accent-surface-soft)' : 'transparent',
                            borderRadius: 16,
                            cursor: 'pointer',
                            textAlign: 'left',
                          }}
                        >
                          <img src={listing.image} alt={listing.name} style={{ width: 56, height: 48, borderRadius: 12, objectFit: 'cover', flexShrink: 0 }} />
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, minWidth: 0 }}>
                            <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 15, fontWeight: active ? 700 : 600, color: active ? 'var(--accent-text-soft)' : 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{listing.name}</span>
                            {listing.locationLabel ? <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 12, color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{listing.locationLabel}</span> : null}
                          </div>
                          {active ? <ChevronRight size={16} strokeWidth={2.4} color="var(--accent-purple-strong)" style={{ transform: 'rotate(-90deg)', flexShrink: 0 }} /> : null}
                        </motion.button>
                      );
                    })}
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          ) : null}

          {!listingsLoading && !availableListings.length ? (
            <div data-testid="host-invite-no-listings-state" className="premium-card-compact premium-card-nested" style={{ borderRadius: 24, padding: '18px 16px', textAlign: 'center' }}>
              <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 19, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.18 }}>You don’t have an approved listing ready yet.</p>
              <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 15, color: 'var(--text-2)', margin: '10px 0 0', lineHeight: 1.25 }}>
                {isLimitedAccessHost
                  ? 'Manual invites will unlock automatically once Allure reaches your area. Until then, you can manage your listings and sync settings here.'
                  : 'Connect a listing from My Listings to unlock the calendar flow here.'}
              </p>
              <motion.button type="button" onClick={() => navigate('/host/listings')} data-testid="host-invite-connect-listing-button" className="premium-pill-button" style={{ marginTop: 16, width: '100%', padding: '12px 0', fontFamily: "'Inria Serif', serif", fontSize: 17, cursor: 'pointer' }} whileHover={{ y: -1.5 }} whileTap={{ scale: 0.99 }}>
                Go to My Listings
              </motion.button>
            </div>
          ) : null}

          <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, color: 'var(--text)', textAlign: 'center', marginTop: 20, marginBottom: 0, lineHeight: 1.08 }}>
            Invite a guest manually
          </p>

          <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 16, fontWeight: 700, color: 'var(--text)', textAlign: 'center', marginTop: 14, lineHeight: 1.18 }}>
            <span style={{ display: 'block' }}>Choose the reservation</span>
            <span style={{ display: 'block' }}>check-in and check-out dates</span>
          </p>
          <div ref={rangeFieldRef} style={{ position: 'relative', marginTop: 12 }}>
            <motion.button
              type="button"
              onClick={() => setShowCalendar((prev) => !prev)}
              data-testid="host-invite-range-input"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                borderRadius: 20,
                border: '1.5px solid var(--accent-border-soft)',
                background: 'linear-gradient(180deg, var(--surface) 0%, var(--surface-2) 100%)',
                padding: '16px 18px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                fontFamily: "'Inria Serif', serif",
                fontSize: 18,
                fontWeight: 600,
                color: checkInDate ? 'var(--text)' : 'var(--accent-text-soft)',
                outline: 'none',
                cursor: 'pointer',
                boxShadow: '0 8px 24px rgba(197, 18, 48, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.05)',
              }}
              whileHover={{ y: -1.5, boxShadow: '0 10px 28px rgba(197, 18, 48, 0.20), inset 0 1px 0 rgba(255, 255, 255, 0.05)' }}
              whileTap={{ scale: 0.99 }}
            >
              <CalendarDays size={20} color="var(--accent-purple)" strokeWidth={2.2} />
              {checkInDate ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                  <span>{formatEuropeanDate(checkInDate)}</span>
                  <ArrowRight size={16} color="var(--accent-purple)" strokeWidth={2.4} />
                  <span>{checkoutDate ? formatEuropeanDate(checkoutDate) : 'Select checkout'}</span>
                </span>
              ) : (
                <span>Select dates</span>
              )}
            </motion.button>
            <AnimatePresence initial={false}>
              {showCalendar ? <PremiumRangeDatePicker checkInDate={checkInDate} checkoutDate={checkoutDate} blockedDateSet={blockedDateSet} blockedCheckInDateSet={blockedCheckInDateSet} onChange={({ checkInDate: nextCheckIn, checkoutDate: nextCheckout }) => { setCheckInDate(nextCheckIn); setCheckoutDate(nextCheckout); }} onClose={() => setShowCalendar(false)} /> : null}
            </AnimatePresence>
          </div>

          <p data-testid="host-manual-invite-warning" style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, color: 'var(--text-2)', textAlign: 'center', marginTop: 14, lineHeight: 1.22, maxWidth: 280, marginLeft: 'auto', marginRight: 'auto' }}>
            Airbnb reservations can’t overlap these dates. Dates blocked by your Airbnb sync settings and iCal activity are unavailable here.
          </p>

          {selectedListingLocked ? (
            <div data-testid="host-invite-settings-locked-note" style={{ marginTop: 16, padding: '14px 16px', borderRadius: 18, background: 'var(--status-warning-surface)', border: '1px solid var(--accent-border-soft)' }}>
              <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, fontWeight: 700, color: 'var(--status-warning-text)', margin: 0, lineHeight: 1.25 }}>
                Match {selectedListing?.name || 'this listing'}&rsquo;s Airbnb settings to unlock its calendar sync and manual invites.
              </p>
              <button type="button" data-testid="host-invite-settings-locked-cta" onClick={openSyncSettingsForSelected} style={{ all: 'unset', cursor: 'pointer', marginTop: 8, fontFamily: "'Inria Serif', serif", fontSize: 14, fontWeight: 700, color: '#DD0608', textDecoration: 'underline' }}>
                Match settings
              </button>
            </div>
          ) : null}

          <motion.button
            type="button"
            onClick={handleGenerateInvite}
            disabled={loading || !selectedListing || isLimitedAccessHost || selectedListingLocked}
            data-testid="host-generate-invite-button"
            style={{
              marginTop: 18,
              width: '100%',
              padding: '15px 0',
              borderRadius: 999,
              border: 'none',
              background: (loading || !selectedListing || isLimitedAccessHost || selectedListingLocked)
                ? 'var(--surface-2)'
                : 'linear-gradient(135deg, var(--brand-red) 0%, var(--accent-purple-strong) 100%)',
              color: (loading || !selectedListing || isLimitedAccessHost || selectedListingLocked) ? 'var(--text-3)' : '#FFF',
              fontFamily: "'Inria Serif', serif",
              fontSize: 18,
              fontWeight: 700,
              letterSpacing: 0.3,
              cursor: (loading || !selectedListing || isLimitedAccessHost || selectedListingLocked) ? 'default' : 'pointer',
              opacity: loading || !selectedListing || isLimitedAccessHost || selectedListingLocked ? 0.6 : 1,
              boxShadow: (loading || !selectedListing || isLimitedAccessHost || selectedListingLocked)
                ? 'none'
                : '0 10px 28px rgba(197, 18, 48, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.18)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 10,
              transition: 'transform 160ms ease, box-shadow 160ms ease, opacity 160ms ease',
            }}
            whileHover={(loading || !selectedListing || isLimitedAccessHost || selectedListingLocked) ? undefined : { y: -1.5, boxShadow: '0 14px 32px rgba(197, 18, 48, 0.45), inset 0 1px 0 rgba(255, 255, 255, 0.22)' }}
            whileTap={(loading || !selectedListing || isLimitedAccessHost || selectedListingLocked) ? undefined : { scale: 0.98 }}
          >
            <span>{loading ? 'Generating…' : 'Generate Invite'}</span>
            {loading ? null : <ArrowRight size={20} strokeWidth={2.4} />}
          </motion.button>

          <AnimatePresence initial={false}>
          {generatedInvite ? (
            <motion.div ref={generatedInviteRef} data-testid="host-generated-invite-card" className="premium-card-soft" style={{ marginTop: 22, padding: '18px 16px 16px', boxShadow: 'var(--premium-shadow-nested)', scrollMarginTop: 16, scrollMarginBottom: 120 }} initial={{ opacity: 0, y: 14, scale: 0.985 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.985 }} transition={{ duration: 0.24, ease: [0.25, 0.1, 0.25, 1] }}>
              <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 17, fontWeight: 700, textAlign: 'center', margin: 0, color: 'var(--text)', lineHeight: 1.18 }}>Share this link with your guests to unlock exclusive discounts during their stay.</p>
              <motion.div className="premium-card-compact premium-card-nested" style={{ marginTop: 16, color: 'var(--text)', padding: '12px 14px', fontFamily: "'Inria Serif', serif", fontSize: 16, textAlign: 'center', wordBreak: 'break-word', lineHeight: 1.2 }} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18, delay: 0.06 }}>{generatedInvite.link}</motion.div>
              <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
                <motion.button type="button" onClick={handleCopy} data-testid="host-copy-invite-button" className="premium-pill-button" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 0', fontFamily: "'Inria Serif', serif", fontSize: 16, cursor: 'pointer', boxShadow: 'var(--premium-shadow-nested)' }} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18, delay: 0.1 }} whileHover={{ y: -1.5 }} whileTap={{ scale: 0.99 }}><Copy size={18} /> Copy</motion.button>
                <motion.button type="button" onClick={handleShare} data-testid="host-share-invite-button" className="premium-pill-button" style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '12px 0', fontFamily: "'Inria Serif', serif", fontSize: 16, cursor: 'pointer', boxShadow: 'var(--premium-shadow-nested)' }} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.18, delay: 0.14 }} whileHover={{ y: -1.5 }} whileTap={{ scale: 0.99 }}><Share2 size={18} /> Share</motion.button>
              </div>
            </motion.div>
          ) : null}
          </AnimatePresence>
        </div>

        {selectedListing ? (
          <div ref={syncSettingsAnchorRef} className="premium-card" data-testid="host-invite-sync-settings-shell" style={{ width: '100%', marginTop: 14, borderRadius: 28, padding: '18px 14px 16px', display: 'grid', gap: showSyncSettings ? 14 : 0, scrollMarginTop: 16 }}>
            <button
              type="button"
              data-testid="host-invite-sync-settings-toggle"
              onClick={() => setShowSyncSettings((prev) => !prev)}
              style={{
                width: '100%',
                background: 'none',
                border: 'none',
                padding: 0,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 16,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <div style={{ display: 'grid', gap: 8 }}>
                <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 22, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.08 }}>
                  Match your Airbnb settings
                </p>
                <p data-testid="host-invite-sync-settings-copy" style={{ fontFamily: "'Inria Serif', serif", fontSize: 15, color: '#DD0608', margin: 0, lineHeight: 1.3 }}>
                  Keep these the same to avoid false reservation alerts.
                </p>
              </div>
              {showSyncSettings ? <ChevronDown size={24} color="var(--text)" style={{ transform: 'rotate(180deg)', flexShrink: 0 }} /> : <ChevronRight size={24} color="var(--text)" style={{ flexShrink: 0 }} />}
            </button>

            <AnimatePresence initial={false}>
              {showSyncSettings ? (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
                >
                  <HostAirbnbSyncSettingsCard
                    testIdPrefix="host-invite-sync-settings"
                    value={syncSettings}
                    onChange={(nextValue) => {
                      setSyncSettings(nextValue);
                      setSyncSettingsDirty(true);
                    }}
                    showIntroCard={false}
                    footer={(
                      <motion.button
                        type="button"
                        onClick={handleSaveSyncSettings}
                        disabled={syncSettingsSaving || !syncSettingsDirty}
                        data-testid="host-invite-sync-settings-save-button"
                        className="premium-pill-button"
                        style={{ width: '100%', padding: '12px 0', fontFamily: "'Inria Serif', serif", fontSize: 17, cursor: 'pointer', opacity: syncSettingsSaving || !syncSettingsDirty ? 0.72 : 1, boxShadow: 'var(--premium-shadow-nested)' }}
                        whileHover={{ y: -1.5 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        {syncSettingsSaving ? 'Saving...' : 'Save Airbnb settings'}
                      </motion.button>
                    )}
                  />
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        ) : null}
      </div>

      <HostBottomNav />

      <WelcomeTourSheet
        open={tourOpen}
        onClose={handleCloseTour}
        slides={hostWelcomeTourSlides}
        testIdPrefix="host-welcome-tour"
      />
      <WelcomeTourSheet
        open={pageTourOpen}
        onClose={handleClosePageTour}
        slides={hostInviteTourSlides}
        testIdPrefix="host-invite-page-tour"
      />

      <Dialog open={Boolean(manualCopyLink)} onOpenChange={(open) => { if (!open) setManualCopyLink(null); }}>
        <DialogContent
          data-testid="host-invite-manual-copy-dialog"
          style={{ fontFamily: "'Inria Serif', serif" }}
        >
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Inria Serif', serif", color: '#1A1226' }}>
              Copy invitation link
            </DialogTitle>
            <DialogDescription style={{ fontFamily: "'Inria Serif', serif", color: '#5A5570' }}>
              Long-press the link below and tap Copy, or tap the Copy button to try again.
            </DialogDescription>
          </DialogHeader>
          <input
            ref={manualLinkInputRef}
            data-testid="host-invite-manual-copy-input"
            type="text"
            readOnly
            value={manualCopyLink || ''}
            onFocus={(e) => e.target.select()}
            onClick={(e) => e.target.select()}
            style={{
              width: '100%',
              padding: '12px 14px',
              borderRadius: 12,
              border: '1px solid rgba(0,0,0,0.15)',
              fontFamily: 'monospace',
              fontSize: 13,
              boxSizing: 'border-box',
              wordBreak: 'break-all',
            }}
          />
          <DialogFooter>
            <button
              data-testid="host-invite-manual-copy-close"
              onClick={() => setManualCopyLink(null)}
              style={{
                padding: '10px 16px',
                borderRadius: 10,
                border: '1px solid var(--card-border)',
                background: 'var(--surface)',
                color: 'var(--text)',
                fontFamily: "'Inria Serif', serif",
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Done
            </button>
            <button
              data-testid="host-invite-manual-copy-retry"
              onClick={handleManualCopyRetry}
              style={{
                padding: '10px 16px',
                borderRadius: 10,
                border: 'none',
                background: 'var(--accent-purple)',
                color: '#FFFFFF',
                fontFamily: "'Inria Serif', serif",
                fontSize: 15,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Copy
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
